import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { passwordError } from "../lib/passwordPolicy";
import { PasswordRules } from "../components/PasswordRules";
import { API } from "../App";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("This reset link is invalid or missing.");
    }
  }, [token]);

  const passwordsMatch = password && password === confirm;
  const passwordValid = !passwordError(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error(passwordError(password));
      return;
    }
    if (!passwordsMatch) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#6B4E8A] mb-6 transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        <div className="bg-white rounded-3xl border border-[#E5DFEC] shadow-sm p-8">
          <div className="flex justify-center mb-6">
            <img src="/dhdh-logo.png" alt="DHDH" className="w-16 h-16 rounded-full object-cover" />
          </div>

          {!token ? (
            <div className="text-center" data-testid="reset-password-no-token">
              <div className="w-14 h-14 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-[#EF4444]" />
              </div>
              <h2 className="text-xl font-medium text-[#2A2A2A] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Invalid reset link
              </h2>
              <p className="text-sm text-[#64748B] mb-6">
                This page needs a valid reset token. Please request a new password reset link.
              </p>
              <Link to="/forgot-password">
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full">
                  Request new link
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-[#10B981]" />
              </div>
              <h2 className="text-xl font-medium text-[#2A2A2A] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Password reset
              </h2>
              <p className="text-sm text-[#64748B]">
                Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-medium text-[#2A2A2A] text-center mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Choose a new password
              </h1>
              <p className="text-sm text-[#64748B] text-center mb-8 leading-relaxed">
                Pick a strong password — at least 10 characters with uppercase, lowercase, a number and a special character.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password" className="text-sm text-[#475569]">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={10}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={submitting}
                    className="mt-1.5 bg-[#FAF7F2] border-[#E5DFEC] rounded-xl"
                    data-testid="reset-password-input"
                  />
                  <PasswordRules password={password} testId="reset-password-rules" />
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-sm text-[#475569]">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    disabled={submitting}
                    className="mt-1.5 bg-[#FAF7F2] border-[#E5DFEC] rounded-xl"
                    data-testid="reset-password-confirm-input"
                  />
                  {confirm && !passwordsMatch && (
                    <p className="text-xs text-[#EF4444] mt-1.5">Passwords don't match</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !passwordValid || !passwordsMatch}
                  className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] disabled:bg-[#94A3B8] rounded-full py-6 font-medium"
                  data-testid="reset-password-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
